import { api } from './client'

/**
 * Permissões vêm calculadas pelo back. O front NÃO deriva permissão de perfil:
 * duplicar a regra aqui garante que ela vai divergir do servidor na primeira
 * mudança, e a versão que engana é sempre a do cliente.
 */
export type Permissao =
  | 'UsuarioVer'
  | 'UsuarioGerenciar'
  | 'SessaoGerenciar'
  | 'ParametroVer'
  | 'ParametroGerenciar'
  | 'ParametroCtgf'
  | 'AcervoVer'
  | 'AuditoriaVer'
  | 'SmtpGerenciar'
  | 'EntregaVer'
  | 'EntregaGerenciar'
  | 'ArquivoVer'
  | 'ArquivoGerenciar'
  | 'AvaliacaoRegistrar'
  | 'AvaliacaoVer'
  | 'ModeloEnviar'
  | 'IaLiberar'

export type Perfil = 'Leitura' | 'Operacao' | 'Administrador'

export type UsuarioSessao = {
  id: number
  email: string
  nome: string
  perfil: Perfil
  segundoFatorAtivo: boolean
  permissoes: Permissao[]
  /**
   * A sessão é a do painel embutido no Chatwoot, e não a do login do portal (277 · F1).
   * O cookie é o mesmo das duas: a tela do portal aberta pelo Chatwoot chega com ela, e a
   * guarda oferece o login em vez de "sem permissão".
   */
  embutida?: boolean
}

export type RespostaEntrada = {
  segundoFator: boolean
  referencia?: string
  usuario?: UsuarioSessao
}

export type UsuarioNaLista = {
  id: number
  email: string
  nome: string
  perfil: Perfil
  ativo: boolean
  segundoFatorAtivo: boolean
  senhaDefinida: boolean
  ultimoAcessoEm: string | null
  sessoesAbertas: number
}

export type ConfiguracaoSmtp = {
  servidor: string
  porta: number
  seguranca: string
  usuario: string | null
  remetenteEmail: string
  remetenteNome: string
  urlBasePortal: string
  senhaDefinida: boolean
}

export const acesso = {
  entrar: (email: string, senha: string) =>
    api.post<RespostaEntrada>('/acesso/entrar', { email, senha }),

  segundoFator: (referencia: string, codigo: string) =>
    api.post<{ usuario: UsuarioSessao }>('/acesso/segundo-fator', { referencia, codigo }),

  sair: () => api.post<void>('/acesso/sair', {}),

  eu: () => api.get<UsuarioSessao>('/acesso/eu'),

  recuperar: (email: string) => api.post<void>('/acesso/recuperar', { email }),

  definirSenha: (token: string, senha: string) =>
    api.post<void>('/acesso/definir-senha', { token, senha }),

  trocarSenha: (senhaAtual: string, novaSenha: string) =>
    api.post<void>('/acesso/trocar-senha', { senhaAtual, novaSenha }),

  codigosEmergencia: () => api.post<{ codigos: string[] }>('/acesso/codigos-emergencia', {}),
}

export const usuarios = {
  listar: () => api.get<UsuarioNaLista[]>('/usuarios'),

  criar: (dados: { email: string; nome: string; perfil: Perfil; segundoFatorAtivo: boolean }) =>
    api.post<{ id: number; convite: string }>('/usuarios', dados),

  alterar: (
    id: number,
    dados: { nome: string; perfil: Perfil; segundoFatorAtivo: boolean; ativo: boolean },
  ) => api.put<void>(`/usuarios/${id}`, dados),

  reenviarConvite: (id: number) => api.post<void>(`/usuarios/${id}/reenviar-convite`, {}),

  revogarSessoes: (id: number) => api.delete<{ encerradas: number }>(`/usuarios/${id}/sessoes`),
}

export const smtp = {
  ler: () => api.get<ConfiguracaoSmtp>('/configuracao/smtp'),

  gravar: (dados: Omit<ConfiguracaoSmtp, 'senhaDefinida'> & { senha: string | null }) =>
    api.put<void>('/configuracao/smtp', dados),

  testar: (
    configuracao: Omit<ConfiguracaoSmtp, 'senhaDefinida'> & { senha: string | null },
    destinatario: string,
  ) => api.post<{ sucesso: boolean; erro?: string }>('/configuracao/smtp/testar', {
    configuracao,
    destinatario,
  }),
}
